import React, { useContext, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormDateField,
  FormField,
  FormReadonlyField,
  FormSelectField,
  FormTextareaField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Spinner } from '@/components/ui/spinner'
import { CountryDto } from 'api/Identity/Types/Country'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { SimpleUserResponse } from 'api/Identity/Types/UserResponse'
import {
  isDocumentData,
  isDormantReactivationData,
  isKycUpdateData,
  isTransactionData,
  TicketDetailsDocumentDataDto,
  TicketDetailsDto,
} from 'api/Tickets/Types/TicketDetails'
import { UpdateTicket } from 'api/Tickets/Types/UpdateTicket'
import { Form, FormikProps, useFormikContext, withFormik } from 'formik'
import omit from 'lodash/omit'
import { ChevronDown, File } from 'lucide-react'
import { useAppSelector } from 'store'
import { useApiClient } from 'utils/api/ApiClient'
import { DocumentApiClient } from 'utils/api/documentApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { isFileTypeImage } from 'utils/check-file-type'
import { stripTimeFromFormat } from 'utils/date'
import { links } from 'utils/featureToggles'
import { showMediaWindow } from 'utils/media-window'
import { hasPermission } from 'utils/permissionsValidator'

import { TicketsViewType } from '../TicketsViewType'
import {
  TicketDetailsDocumentValues,
  TicketDetailsKycUpdateValues,
  TicketDetailsTransactionValues,
  TicketDetailsValues,
} from './types'

const formatPhoneFieldValue = (phoneCountryCode?: string | null, phoneNumber?: string | null) =>
  [phoneCountryCode, phoneNumber].filter((value) => Boolean(value?.trim())).join(' ')

const TicketDetailsUI = (props: TicketDetailsProps & FormikProps<TicketDetailsValues>) => {
  const { resetForm, submitForm, setFieldValue, values, data, permissions } = props
  const [auth] = useContext(AuthSessionContext)
  const dateFormatType = auth?.user.dateFormatType.name
  const [isEdit, setIsEdit] = useState<boolean>(false)

  const statuses = useAppSelector((state) => state.tickets.statuses) ?? []

  const isSalesTicket = props.viewType === TicketsViewType.Sales
  const isPaymentTicket = props.viewType === TicketsViewType.Payments
  const shouldRenderTicketDataFieldsFactory =
    isTransactionData(data.data) ||
    isDocumentData(data.data) ||
    isKycUpdateData(data.data) ||
    isDormantReactivationData(data.data)

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    await submitForm()
    setIsEdit(false)
  }

  const handleCancel = () => {
    resetForm({
      values: {
        ...props.data,
        assignee: {
          id: props?.data?.assignee?.id ?? '',
          name: props?.data?.assignee?.name ?? '',
        },
      },
    })
    setIsEdit(false)
  }

  const renderMenu = () => (
    <>
      <DropdownMenuItem
        onSelect={() => props.setAuditLogModal(true)}
        data-test='audit-log-menu-button'
      >
        Audit Log
      </DropdownMenuItem>
      <DropdownMenuItem
        disabled={!props.shouldBeTicketTypeTranslationAssigneeVisible}
        onSelect={() => props.setAssigneeModal(true)}
        data-test='translation-menu-button'
      >
        Request Translation
      </DropdownMenuItem>
    </>
  )

  return (
    <Form onSubmit={handleSave}>
      <DataGrid
        rightAddon={
          <ButtonGroup>
            {!isEdit ? (
              <Button
                type='button'
                onClick={() => setIsEdit(true)}
                disabled={!hasPermission(permissions, PermissionsEnum.CanEditTicket)}
                data-test='edit-button'
              >
                Edit
              </Button>
            ) : (
              <>
                <Button
                  type='button'
                  variant='outline'
                  onClick={handleCancel}
                  data-test='cancel-button'
                >
                  Cancel
                </Button>
                <Button type='submit' disabled={props.isSubmitting} data-test='save-button'>
                  Save
                  {props.isSubmitting && <Spinner data-icon='inline-start' />}
                </Button>
              </>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    type='button'
                    variant='outline'
                    size='icon'
                    aria-label='More options'
                    data-test='more-button'
                  />
                }
              >
                <ChevronDown />
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>{renderMenu()}</DropdownMenuContent>
            </DropdownMenu>
          </ButtonGroup>
        }
      >
        <DataColumn>
          <DataCard title='Main' columns={2}>
            <FormReadonlyField label='Number' value={values.number} dataTest='number-field' />
            <FormField
              label='Description'
              name='description'
              readOnly={!isEdit}
              dataTest='description-field'
            />
            <FormSelectField
              options={props.users}
              label='Assignee'
              name='assignee'
              readOnly={!isEdit}
              showSearch
              disabled={!hasPermission(permissions, PermissionsEnum.CanAssignTickets)}
            />
            {!isSalesTicket && props.values.client && (
              <FormReadonlyField
                label='Client'
                value={props.values.client.name ?? ''}
                dataTest='client-link'
                link={generatePath(links.recordsClients.detailsUrl as string, {
                  id: props.values.client?.id,
                  slug: 'details',
                })}
              />
            )}

            {!isSalesTicket && data.lead && (
              <FormReadonlyField
                label='Lead'
                value={data.lead.name}
                dataTest='lead-link'
                link={generatePath(links.recordsLeads.detailsUrl as string, {
                  id: data.lead?.id,
                  slug: 'details',
                })}
              />
            )}

            {isSalesTicket && data.lead && (
              <FormReadonlyField
                label='Phone Number'
                value={data.lead.phoneNumber}
                dataTest='phoneNumber-field'
              />
            )}

            {isSalesTicket && data.client && (
              <FormReadonlyField
                label='Country'
                value={data.client.country.name}
                dataTest='phoneNumber-field'
              />
            )}

            <FormSelectField options={statuses} label='Status' name='status' readOnly={!isEdit} />

            <FormReadonlyField label='Type' value={values.type.name} dataTest='type-field' />

            {'previousStatus' in data.data && (
              <FormReadonlyField
                label='Previous Status'
                value={data.data.previousStatus?.name ?? ''}
                dataTest='type-field'
              />
            )}

            {'nextStatus' in data.data && (
              <FormReadonlyField
                label='Next Status'
                value={data.data.nextStatus?.name ?? ''}
                dataTest='type-field'
              />
            )}

            <FormTextareaField
              label='Comment'
              name='comment'
              readOnly={!isEdit}
              dataTest='comment-field'
            />
          </DataCard>

          <DataCard title='Ticket Data' columns={2}>
            {shouldRenderTicketDataFieldsFactory && (
              <FieldsFactory
                setDocumentPreviewModal={props.setDocumentPreviewModal}
                isPaymentTicket={isPaymentTicket}
              />
            )}

            {'firstName' in data.data && (
              <FormField label='First Name' name='data.firstName' readOnly={!isEdit} />
            )}

            {'middleName' in data.data && (
              <FormField label='Middle Name' name='data.middleName' readOnly={!isEdit} />
            )}

            {'lastName' in data.data && (
              <FormField label='Last Name' name='data.lastName' readOnly={!isEdit} />
            )}

            {'state' in data.data && (
              <FormField label='State' name='data.state' readOnly={!isEdit} />
            )}

            {'street' in data.data && (
              <FormField label='Address line' name='data.street' readOnly={!isEdit} />
            )}

            {'streetHouseNo' in data.data && (
              <FormField
                label='Street / House Number'
                name='data.streetHouseNo'
                readOnly={!isEdit}
              />
            )}

            {'city' in data.data && <FormField label='City' name='data.city' readOnly={!isEdit} />}
            {'country' in data.data && (
              <FormSelectField
                options={props.countries}
                label='Country'
                value={data.data.country?.id || undefined}
                readOnly={!isEdit}
                onChange={(value) => {
                  setFieldValue('data.countryId', value ? String(value) : '')
                  setFieldValue('data.country.id', value ? String(value) : '')
                }}
              />
            )}

            {'notes' in data.data && (
              <FormField label='Notes' name='data.notes' readOnly={!isEdit} />
            )}

            {'placeOfBirth' in data.data && (
              <FormField label='Place Of Birth' name='data.placeOfBirth' readOnly={!isEdit} />
            )}

            {'dateOfBirth' in data.data && (
              <FormDateField
                label='Date Of Birth'
                name='data.dateOfBirth'
                readOnly={!isEdit}
                dateFormatType={stripTimeFromFormat(dateFormatType)}
                onChange={(value) => setFieldValue('data.dateOfBirth', value || null)}
              />
            )}

            {'postalCode' in data.data && (
              <FormField label='Postal Code' name='data.postalCode' readOnly={!isEdit} />
            )}

            {'expiryDate' in data.data && (
              <FormDateField
                label='Expiration Date'
                name='data.expiryDate'
                readOnly={!isEdit}
                dateFormatType={stripTimeFromFormat(dateFormatType)}
                onChange={(value) =>
                  setFieldValue('data.expiryDate', value?.toISOString().slice(0, 10) || null)
                }
              />
            )}

            {'dateOfIssue' in data.data && (
              <FormDateField
                label='Date Of Issue'
                name='data.dateOfIssue'
                readOnly={!isEdit}
                dateFormatType={stripTimeFromFormat(dateFormatType)}
                onChange={(value) =>
                  setFieldValue('data.dateOfIssue', value?.toISOString().slice(0, 10) || null)
                }
              />
            )}

            {data.relatesTo && (
              <FormReadonlyField
                label='Related Document Ticket'
                value={data.relatesTo.number ?? ''}
                dataTest='client-link'
                link={generatePath(
                  props.viewType === TicketsViewType.BackOffice
                    ? (links.ticketsTranslation.detailsUrl as string)
                    : (links.ticketsBackoffice.detailsUrl as string),
                  {
                    id: data.relatesTo?.id,
                  }
                )}
              />
            )}
          </DataCard>
        </DataColumn>
      </DataGrid>
    </Form>
  )
}

interface FieldsFactoryProps {
  setDocumentPreviewModal({ id, name }: { id: string; name: string }): void
  isPaymentTicket?: boolean
}

const FieldsFactory: React.FC<FieldsFactoryProps> = ({
  setDocumentPreviewModal,
  isPaymentTicket,
}) => {
  const { values } = useFormikContext<TicketDetailsValues>()

  if (isTransactionData(values.data)) {
    return <TransactionComponent />
  }

  if (isDocumentData(values.data)) {
    return (
      <DocumentComponent
        setDocumentPreviewModal={setDocumentPreviewModal}
        isPaymentTicket={isPaymentTicket}
      />
    )
  }

  if (isKycUpdateData(values.data)) {
    return <KycUpdateComponent setDocumentPreviewModal={setDocumentPreviewModal} />
  }

  if (isDormantReactivationData(values.data)) {
    return <DormantReactivationComponent />
  }

  return null
}

const TransactionComponent = () => {
  const { values } = useFormikContext<TicketDetailsValues & TicketDetailsTransactionValues>()

  return (
    <>
      <FormReadonlyField
        label='Transaction'
        value={values.data.transaction.name ?? ''}
        dataTest='transaction-link'
        link={generatePath('/transactions/:id', {
          id: values.data.transaction.id,
        })}
      />

      {values.data?.transaction?.groupReferenceId && (
        <FormReadonlyField
          label='Group Reference'
          value={values.data.transaction.groupReferenceId}
        />
      )}
    </>
  )
}

const DocumentComponent: React.FC<FieldsFactoryProps> = ({
  setDocumentPreviewModal,
  isPaymentTicket,
}) => {
  const documentApiClient = useApiClient(DocumentApiClient)
  const { values } = useFormikContext<TicketDetailsValues & TicketDetailsDocumentValues>()

  const showDocumentPreviewModal = async (event: React.MouseEvent) => {
    event.preventDefault()
    const documentFile = await documentApiClient.getDocumentUrl(values.data.document.id)

    if (documentFile?.url && documentFile?.filename && isFileTypeImage(documentFile.filename)) {
      showMediaWindow(documentFile.url, documentFile.filename)
    } else {
      setDocumentPreviewModal({ id: values.data.document.id, name: values.data.document.name })
    }
  }

  return (
    <>
      <FormReadonlyField
        label='Document'
        value={values.data.document.name ?? ''}
        dataTest='document-field'
        rightAddon={
          <Button size='icon-xs' onClick={(event) => void showDocumentPreviewModal(event)}>
            <File />
          </Button>
        }
      />

      {isPaymentTicket && (
        <FormReadonlyField label='Document Status' value={values.data.document?.status?.name} />
      )}
    </>
  )
}

const KycUpdateComponent: React.FC<FieldsFactoryProps> = ({ setDocumentPreviewModal }) => {
  const { values } = useFormikContext<TicketDetailsValues & TicketDetailsKycUpdateValues>()
  const kycUpdate = values.data.kycUpdate as TicketDetailsKycUpdateValues['data']['kycUpdate'] & {
    phoneCountryCode?: string | null
  }

  const showDocumentPreviewModal = (event: React.MouseEvent): void => {
    event.preventDefault()
    setDocumentPreviewModal({
      id: kycUpdate.document.id,
      name: kycUpdate.document.name,
    })
  }

  return [
    <FormReadonlyField
      label='Phone Number'
      value={formatPhoneFieldValue(kycUpdate.phoneCountryCode, kycUpdate.phoneNumber)}
      dataTest='phone-number-field'
    />,

    <FormReadonlyField label='State' value={kycUpdate.state} dataTest='state-field' />,
    <FormReadonlyField label='City' value={kycUpdate.city} dataTest='city-field' />,
    <FormReadonlyField label='Address line' value={kycUpdate.street} dataTest='street-field' />,
    <FormReadonlyField
      label='Street / House Number'
      value={kycUpdate.streetHouseNo}
      dataTest='streetHouseNo-field'
    />,
    <FormReadonlyField label='Zip Code' value={kycUpdate.zipCode} dataTest='zip-code-field' />,
    <FormReadonlyField
      label='TIN'
      value={kycUpdate.taxIdentificationNumber}
      dataTest='tax-identification-number-field'
    />,

    ...[
      values.client && kycUpdate.kycTestId && (
        <FormReadonlyField
          label='KYC Test'
          value={kycUpdate.kycTestId ?? ''}
          dataTest='tests-kyc'
          link={generatePath(links.recordsClients.detailsUrl as string, {
            id: values.client?.id,
            slug: 'tests-kyc',
          })}
        />
      ),
    ],

    ...[
      kycUpdate.document?.name && (
        <FormReadonlyField
          label='Document'
          value={kycUpdate.document.name ?? ''}
          dataTest='document-field'
          rightAddon={
            <Button size='icon-xs' onClick={showDocumentPreviewModal}>
              <File />
            </Button>
          }
        />
      ),
    ],
  ]
}

const DormantReactivationComponent: React.FC = () => {
  const { values } = useFormikContext<TicketDetailsValues>()

  if (!('dormantUpdate' in values.data)) {
    return null
  }

  const dormantUpdate = values.data.dormantUpdate as {
    phoneCountryCode?: string | null
    phoneNumber?: string | null
    email?: string | null
    state?: string | null
    city?: string | null
    street?: string | null
    zipCode?: string | null
    taxIdentificationNumber?: string | null
  }

  return (
    <>
      <FormReadonlyField
        label='Phone Number'
        value={formatPhoneFieldValue(dormantUpdate.phoneCountryCode, dormantUpdate.phoneNumber)}
        dataTest='phone-number-field'
      />

      <FormReadonlyField label='Email' value={dormantUpdate.email ?? ''} dataTest='email-field' />
      <FormReadonlyField label='State' value={dormantUpdate.state ?? ''} dataTest='state-field' />
      <FormReadonlyField label='City' value={dormantUpdate.city ?? ''} dataTest='city-field' />
      <FormReadonlyField
        label='Street'
        value={dormantUpdate.street ?? ''}
        dataTest='street-field'
      />
      <FormReadonlyField
        label='Zip Code'
        value={dormantUpdate.zipCode ?? ''}
        dataTest='zip-code-field'
      />
      <FormReadonlyField
        label='TIN'
        value={dormantUpdate.taxIdentificationNumber ?? ''}
        dataTest='tax-identification-number-field'
      />
    </>
  )
}

interface TicketDetailsProps {
  data: TicketDetailsDto
  viewType: TicketsViewType
  users: SimpleUserResponse[]
  countries: CountryDto[]
  shouldBeTicketTypeTranslationAssigneeVisible?: boolean
  permissions: PermissionDto[]

  handleUpdate(values: UpdateTicket): void

  setDocumentPreviewModal({ id, name }: { id: string; name: string }): void

  setAuditLogModal(value: boolean): void

  setAssigneeModal(value: boolean): void
}

function dataToSubmit(values: TicketDetailsValues, ticket: TicketDetailsDto): UpdateTicket {
  const data = {
    id: values.id,
    statusId: values.status.id,
    description: values?.description ?? '',
    assigneeId: values?.assignee?.id ?? '',
    comment: values?.comment ?? '',
  }

  if (ticket.viewType.id === TicketsViewType.Translation && 'country' in values.data) {
    const translationFields = omit(
      { ...values.data, countryId: values.data.country.id },
      'document',
      'country'
    ) as TicketDetailsDocumentDataDto
    return {
      ...data,
      translationFields,
    }
  }

  if (ticket.viewType.id === TicketsViewType.Translation) {
    const translationFields = omit(
      { ...values.data },
      'document',
      'country'
    ) as TicketDetailsDocumentDataDto
    return {
      ...data,
      translationFields,
    }
  }

  return data
}

export const TicketDetailsForm = withFormik<TicketDetailsProps, TicketDetailsValues>({
  mapPropsToValues: (props) => {
    return {
      ...props.data,
      description: props.data.description,
      assignee: {
        id: props.data?.assignee?.id ?? '',
        name: props.data?.assignee?.name ?? '',
      },
    }
  },
  handleSubmit: async (values: TicketDetailsValues, { props, setSubmitting }) => {
    setSubmitting(true)
    await props.handleUpdate(dataToSubmit(values, props.data))
    setSubmitting(false)
  },
  enableReinitialize: true,
})(TicketDetailsUI)
